import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation, Trans } from 'react-i18next';
import api from '../../utils/api';
import { connectSocket, disconnectSocket } from '../../utils/socket';
import useViolationDetector from '../../hooks/useViolationDetector';
import useFaceDetection from '../../hooks/useFaceDetection';
import useExamStore from '../../store/examStore';
import useAuthStore from '../../store/authStore';
import ViolationWarning from '../../components/ViolationWarning';
import useIsMobile from '../../hooks/useIsMobile';
import { detectIncognito } from '../../utils/detectIncognito';
import { Button, Spinner, Modal } from '../../components/ui';
import {
  Menu, X, Clock, MessageSquare, AlertTriangle, Flag, Camera, Video,
  CheckCircle2, ChevronLeft, ChevronRight, Lock, Lightbulb, Armchair,
  VolumeX, Smartphone, BookX, Eye, Globe, Keyboard, MousePointerClick,
  ShieldOff, ShieldCheck, RotateCcw, Loader2, Check,
} from 'lucide-react';

const formatTime = (seconds) => {
  if (!seconds || seconds <= 0) return '00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const ExamRoom = () => {
  const { t } = useTranslation();
  const { examId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { setExam, setAttempt, addViolation, addMessage, clearExam, violations, messages, currentExam, attempt } = useExamStore();

  const [exam, setExamLocal] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [currentViolation, setCurrentViolation] = useState(null);
  const [violationCount, setViolationCount] = useState(0);
  const [attemptId, setAttemptId] = useState(null);
  const [examLocked, setExamLocked] = useState(false);
  const [proctorMessages, setProctorMessages] = useState([]);
  const [showMessages, setShowMessages] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lobbyInfo, setLobbyInfo] = useState(null);   // { name, startTime, endTime, duration }
  const [waitingToStart, setWaitingToStart] = useState(false);
  const [secsUntilStart, setSecsUntilStart] = useState(0);
  const [showNavDrawer, setShowNavDrawer] = useState(false);
  const isMobile = useIsMobile(640);

  const socketRef = useRef(null);
  const autoSubmitRef = useRef(false);
  const timeLeftRef = useRef(0);
  const shownRemindersRef = useRef(new Set());

  // Setup stages: 'permissions' → 'preview' → done (streamSetupComplete=true)
  const [streamSetupComplete, setStreamSetupComplete] = useState(false);
  const [setupStage, setSetupStage] = useState('permissions'); // 'permissions' | 'preview'
  const [requestingStreams, setRequestingStreams] = useState(false);
  const [capturingPhoto, setCapturingPhoto] = useState(false);
  const [initialWebcamSnap, setInitialWebcamSnap] = useState(null);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const initialSnapsRef = useRef({ webcam: null });

  const webcamVideoRef = useRef(null);
  const webcamStreamRef = useRef(null);
  const streamIntervalRef = useRef(null);  // snapshot every 60s
  const heartbeatIntervalRef = useRef(null); // time update every 3s
  const answersRef = useRef({});      // always-current answers for heartbeat closure
  const questionsRef = useRef([]);    // always-current questions for heartbeat closure

  // Persist webcam stream across React conditional re-renders
  useEffect(() => {
    if (webcamVideoRef.current && webcamStreamRef.current && webcamVideoRef.current.srcObject !== webcamStreamRef.current) {
      webcamVideoRef.current.srcObject = webcamStreamRef.current;
      webcamVideoRef.current.play().catch(() => { });
    }
  });

  const playAudioAlert = useCallback(() => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const ctx = new AudioContext();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(440, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } catch (e) { }
  }, []);

  // Cleanup Streams
  useEffect(() => {
    return () => {
      if (webcamStreamRef.current) {
        webcamStreamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  // Fetch lightweight exam info for the pre-exam lobby (name, startTime, etc.)
  useEffect(() => {
    const fetchLobby = async () => {
      try {
        const res = await api.get(`/exams/${examId}/lobby`);
        setLobbyInfo(res.data.data);
      } catch {
        // Non-fatal — exam room will still work; startExam will enforce timing
      }
    };
    fetchLobby();
  }, [examId]); // eslint-disable-line

  // Countdown tick while waiting for exam startTime
  useEffect(() => {
    if (!waitingToStart || !lobbyInfo?.startTime) return;
    const tick = setInterval(() => {
      const secs = Math.ceil((new Date(lobbyInfo.startTime).getTime() - Date.now()) / 1000);
      if (secs <= 0) {
        clearInterval(tick);
        setWaitingToStart(false);
        setStreamSetupComplete(true);
      } else {
        setSecsUntilStart(secs);
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [waitingToStart, lobbyInfo]); // eslint-disable-line

  // Wait until a video element has valid dimensions (stream is playing)
  const waitForVideo = (videoEl, timeout = 8000) =>
    new Promise((resolve, reject) => {
      if (!videoEl) { reject(new Error('no video el')); return; }
      if (videoEl.videoWidth > 0 && videoEl.readyState >= 2) { resolve(); return; }

      const onReady = () => { clearTimeout(t); resolve(); };
      videoEl.addEventListener('playing', onReady, { once: true });
      videoEl.addEventListener('loadeddata', onReady, { once: true });
      videoEl.play().catch(() => { });

      const t = setTimeout(() => {
        videoEl.removeEventListener('playing', onReady);
        videoEl.removeEventListener('loadeddata', onReady);
        reject(new Error('timeout'));
      }, timeout);
    });

  // Capture a single frame from a video element as a base64 JPEG
  const captureFrameNow = (videoEl, width = 640) => {
    if (!videoEl || !videoEl.videoWidth) return null;
    try {
      const canvas = document.createElement('canvas');
      const aspect = videoEl.videoWidth / videoEl.videoHeight;
      canvas.width = width;
      canvas.height = Math.round(width / aspect);
      canvas.getContext('2d').drawImage(videoEl, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/jpeg', 0.85);
    } catch { return null; }
  };

  const requestStreams = async () => {
    // Converts the honor-system "not incognito" checkbox into an actual check
    // (best-effort — see detectIncognito.js for why this can't be guaranteed).
    const incognito = await detectIncognito();
    if (incognito) {
      toast.error(t('examRoom.toasts.incognitoWarning'), { duration: 7000 });
      return;
    }

    setRequestingStreams(true);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error(t('examRoom.toasts.mediaUnavailable'), { duration: 6000 });
        return;
      }

      // Camera + mic — REQUIRED
      try {
        const webcam = await navigator.mediaDevices.getUserMedia({ video: { width: 1280, height: 720 }, audio: true });
        webcamStreamRef.current = webcam;
        if (webcamVideoRef.current) webcamVideoRef.current.srcObject = webcam;
      } catch (err) {
        toast.error(err.name === 'NotAllowedError'
          ? t('examRoom.toasts.cameraDenied')
          : t('examRoom.toasts.noCamera'));
        return;
      }

      setSetupStage('preview');
    } finally {
      setRequestingStreams(false);
    }
  };

  // Called when student clicks "Capture Photo"
  const captureInitialSnapshots = async () => {
    setCapturingPhoto(true);
    try {
      // Force video to play before capturing
      webcamVideoRef.current?.play().catch(() => { });

      // Wait for webcam to be ready
      try { await waitForVideo(webcamVideoRef.current, 5000); } catch { /* proceed anyway */ }

      // Additional short delay to allow frame buffer to fill
      await new Promise(r => setTimeout(r, 500));

      const webcamSnap = captureFrameNow(webcamVideoRef.current, 640);

      if (!webcamSnap) {
        toast.error(t('examRoom.toasts.captureFailed'));
        return;
      }

      setInitialWebcamSnap(webcamSnap);
      initialSnapsRef.current = { webcam: webcamSnap };
    } finally {
      setCapturingPhoto(false);
    }
  };

  // Called when student clicks "Start Exam" after photo capture
  const confirmAndStartExam = () => {
    // Catches DevTools opened before the exam even starts — the keydown
    // blocker only sees *new* open attempts made after this point, and can't
    // stop the browser's native shortcut anyway (see useViolationDetector).
    if (isDevToolsOpen()) {
      toast.error(t('examRoom.toasts.devToolsOpen'), { duration: 6000 });
      return;
    }

    const el = document.documentElement;
    const requestFs = el.requestFullscreen || el.mozRequestFullScreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
    if (requestFs) {
      requestFs.call(el).catch(err => console.error("Fullscreen request failed:", err));
    }

    // If exam hasn't started yet, enter the waiting room instead of loading questions
    if (lobbyInfo?.startTime) {
      const secsLeft = Math.ceil((new Date(lobbyInfo.startTime).getTime() - Date.now()) / 1000);
      if (secsLeft > 0) {
        setSecsUntilStart(secsLeft);
        setWaitingToStart(true);
        return;
      }
    }

    setStreamSetupComplete(true);
  };

  // Initialize exam and attempt
  useEffect(() => {
    const init = async () => {
      if (!streamSetupComplete) return;
      try {
        // Start or resume attempt — returns { attempt, exam, questions }
        const attemptRes = await api.post(`/exams/${examId}/start`);
        // Server detected the attempt already ran out of time — redirect immediately
        if (attemptRes.data.data?.timeExpired) {
          toast.error(t('examRoom.toasts.timeExpired'));
          navigate('/student/dashboard');
          return;
        }
        const { attempt: attemptData, exam: examData, questions: qs } = attemptRes.data.data;
        setExamLocal(examData);
        setExam(examData);
        setAttemptId(attemptData._id || attemptData.id);
        setAttempt(attemptData);
        setQuestions(qs || []);

        // Restore answers from attempt
        if (attemptData.answers?.length) {
          const restored = {};
          attemptData.answers.forEach(a => {
            restored[a.question?.toString() || a.question] = a.answer !== undefined ? a.answer : a.selectedOptions;
          });
          setAnswers(restored);
        }

        // Compute actual remaining time from when the attempt started
        const startedAt = new Date(attemptData.startTime).getTime();
        const totalSecs = (examData.duration || 60) * 60;
        const elapsedSecs = Math.floor((Date.now() - startedAt) / 1000);
        // Also cap against the exam's hard endTime window
        const endTimeRemaining = examData.endTime
          ? Math.floor((new Date(examData.endTime).getTime() - Date.now()) / 1000)
          : Infinity;
        const remaining = Math.min(
          Math.max(0, totalSecs - elapsedSecs),
          Math.max(0, endTimeRemaining)
        );
        setTimeLeft(remaining);

        setExamStarted(true);
      } catch (err) {
        toast.error(err.response?.data?.message || t('examRoom.toasts.loadFailed'));
        navigate('/student/dashboard');
      } finally {
        setLoading(false);
      }
    };
    init();

    return () => {
      clearExam();
    };
  }, [examId, streamSetupComplete]);

  // Socket setup
  useEffect(() => {
    if (!examStarted || !attemptId) return;

    const socket = connectSocket();
    socketRef.current = socket;

    const joinRoom = () => {
      socket.emit('join-exam', { examId, attemptId });
      // Send initial snapshot immediately after joining
      const { webcam } = initialSnapsRef.current;
      if (webcam) {
        socket.emit('student_stream', {
          examId,
          webcamFrame: webcam,
          capturedAt: new Date().toISOString(),
          isInitialSnapshot: true,
        });
      }
    };
    joinRoom();

    // Re-join room if socket reconnects
    socket.on('connect', joinRoom);

    socket.on('proctor-message', (msg) => {
      playAudioAlert();
      addMessage(msg);
      const newMsg = {
        id: msg.messageId || Date.now(),
        content: msg.content || msg.message,
        type: msg.type || 'INFO',
        timestamp: msg.timestamp || new Date().toISOString(),
        read: false,
      };
      setProctorMessages(prev => [newMsg, ...prev]);
      setUnreadCount(prev => prev + 1);
      setShowMessages(true);
    });

    socket.on('exam-locked', (data) => {
      setExamLocked(true);
      toast.error(t('examRoom.toasts.examLocked'));
    });

    socket.on('exam-force-submitted', async (data) => {
      toast.error(t('examRoom.toasts.forceSubmitted'));
      await handleSubmit(true);
    });

    return () => {
      socket.off('connect', joinRoom);
      socket.off('proctor-message');
      socket.off('exam-locked');
      socket.off('exam-force-submitted');
    };
  }, [examStarted, attemptId, examId]);

  // Violation handler
  const handleViolation = useCallback(async (violation) => {
    playAudioAlert();
    const count = violationCount + 1;
    setViolationCount(count);
    setCurrentViolation(violation);
    addViolation(violation);

    // Emit to socket
    if (socketRef.current) {
      socketRef.current.emit('violation', {
        examId,
        attemptId,
        ...violation,
        count
      });
    }

    // Report to API
    try {
      await api.post(`/violations`, {
        examId,
        attemptId,
        type: violation.type,
        description: violation.description
      });
    } catch (err) {
      // continue
    }

    // Auto-submit if exceeded warning limit
    const maxViolations = exam?.warningLimit || 3;
    if (count >= maxViolations) {
      toast.error(t('examRoom.toasts.maxViolations', { max: maxViolations }));
      setTimeout(() => handleSubmit(true), 2000);
    }
  }, [violationCount, examId, attemptId, exam]);

  const { requestFullscreen, isDevToolsOpen } = useViolationDetector({
    isActive: examStarted && !submitting && !examLocked,
    onViolation: handleViolation
  });

  useFaceDetection({
    isActive: examStarted && !submitting && !examLocked,
    videoRef: webcamVideoRef,
    onViolation: handleViolation,
  });

  // Snapshot interval + heartbeat (fullscreen is requested in confirmAndStartExam click handler)
  useEffect(() => {
    if (examStarted) {

      const captureFrame = (videoEl, width = 640, quality = 0.7) => {
        try {
          if (!videoEl || !videoEl.videoWidth) return null;
          const canvas = document.createElement('canvas');
          const aspect = videoEl.videoWidth / videoEl.videoHeight;
          canvas.width = width;
          canvas.height = Math.round(width / aspect);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
          return canvas.toDataURL('image/jpeg', quality);
        } catch (e) { return null; }
      };

      // Live proctoring feed — frequent snapshots (not full WebRTC video) sent
      // over the existing socket connection. Each frame replaces the last one
      // on both the server (in-memory cache, keyed by student) and the
      // proctor's dashboard (keyed state, not an accumulating list) — nothing
      // here is retained as history, by design.
      streamIntervalRef.current = setInterval(() => {
        if (!socketRef.current) return;
        try {
          // Small + fairly compressed on purpose: this tile is one of many in
          // a proctor grid, not a full-screen view — clarity needed to notice
          // a problem, not forensic detail.
          const webcamFrame = captureFrame(webcamVideoRef.current, 240, 0.5);
          if (webcamFrame) {
            socketRef.current.emit('student_stream', {
              examId,
              webcamFrame,
              capturedAt: new Date().toISOString()
            });
          }
        } catch (e) { }
      }, 300);

      // Heartbeat every 3 seconds — send time, progress, and current status
      heartbeatIntervalRef.current = setInterval(() => {
        if (!socketRef.current) return;
        try {
          const ans = answersRef.current;
          const qs = questionsRef.current;
          const answeredCount = qs.filter(q => {
            const id = q._id || q.id;
            const a = ans[id];
            return a !== undefined && a !== '' && !(Array.isArray(a) && a.length === 0);
          }).length;
          socketRef.current.emit('student-status', {
            examId,
            status: 'ACTIVE',
            data: {
              timeRemaining: timeLeftRef.current,
              answeredCount,
              totalQuestions: qs.length,
            }
          });
        } catch (e) { }
      }, 3000);
    }
    return () => {
      if (streamIntervalRef.current) clearInterval(streamIntervalRef.current);
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
    };
  }, [examStarted, examId, requestFullscreen]);

  // Countdown timer — runs once when exam starts, counts timeLeft down every second
  useEffect(() => {
    if (!examStarted || timeLeft <= 0) return;
    const tick = setInterval(() => {
      setTimeLeft(prev => {
        const next = prev - 1;
        timeLeftRef.current = next;
        if (next <= 0) {
          clearInterval(tick);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [examStarted]); // eslint-disable-line

  // Trigger time-up when countdown hits 0
  useEffect(() => {
    if (examStarted && timeLeft === 0 && !autoSubmitRef.current) {
      handleTimeUp();
    }
  }, [timeLeft]); // eslint-disable-line

  // Time reminders — progressively more urgent as deadline approaches
  useEffect(() => {
    if (!examStarted || timeLeft <= 0) return;
    const reminders = [
      { at: 900, msg: t('examRoom.toasts.reminder15'), icon: '⏰', duration: 6000 },
      { at: 600, msg: t('examRoom.toasts.reminder10'), icon: '⏰', duration: 6000 },
      { at: 300, msg: t('examRoom.toasts.reminder5'), icon: '⚠️', duration: 8000 },
      { at: 120, msg: t('examRoom.toasts.reminder2'), icon: '🚨', duration: 10000 },
      { at: 60, msg: t('examRoom.toasts.reminder1'), icon: '🚨', duration: 12000 },
      { at: 30, msg: t('examRoom.toasts.reminder30s'), icon: '🚨', duration: 15000 },
    ];
    for (const r of reminders) {
      if (timeLeft <= r.at && !shownRemindersRef.current.has(r.at)) {
        shownRemindersRef.current.add(r.at);
        playAudioAlert();
        toast(r.msg, { duration: r.duration, icon: r.icon });
      }
    }
  }, [timeLeft, examStarted]); // eslint-disable-line

  // Save answer
  const saveAnswer = useCallback(async (questionId, answer) => {
    try {
      await api.put(`/exams/${examId}/save-answer`, { questionId, answer });
    } catch (err) {
      // continue silently
    }
  }, [examId]);

  const currentQuestion = questions[currentIdx];

  // Keep refs in sync so heartbeat interval always has fresh values
  useEffect(() => { answersRef.current = answers; }, [answers]);
  useEffect(() => { questionsRef.current = questions; }, [questions]);

  const handleAnswerChange = (value) => {
    if (!currentQuestion) return;
    const qId = currentQuestion._id || currentQuestion.id;
    const updated = { ...answers, [qId]: value };
    setAnswers(updated);
    answersRef.current = updated;
    saveAnswer(qId, value);
  };

  const handleMultiSelectChange = (optionId, optionIndex) => {
    if (!currentQuestion) return;
    const qId = currentQuestion._id || currentQuestion.id;
    const current = answers[qId] || [];
    // Match against BOTH the option's _id and its plain index — an attempt
    // already in progress from before options were identified by _id can
    // still have legacy index entries in `current`. Checking _id alone would
    // fail to recognize those as "already selected," re-adding the option as
    // a second, differently-typed entry instead of toggling it off (the
    // backend's scoring now resolves this defensively too, but the toggle
    // itself should behave correctly rather than relying on that fallback).
    const matches = (i) => String(i) === String(optionId) || String(i) === String(optionIndex);
    const isSelected = current.some(matches);
    let updated;
    if (isSelected) {
      updated = current.filter(i => !matches(i));
    } else {
      updated = [...current, optionId];
    }
    setAnswers(prev => ({ ...prev, [qId]: updated }));
    saveAnswer(qId, updated);
  };

  const toggleFlag = () => {
    if (!currentQuestion) return;
    const qId = currentQuestion._id || currentQuestion.id;
    setFlagged(prev => {
      const next = new Set(prev);
      if (next.has(qId)) next.delete(qId);
      else next.add(qId);
      return next;
    });
  };

  const handleSubmit = async (force = false) => {
    if (autoSubmitRef.current) return;
    autoSubmitRef.current = true;
    setSubmitting(true);
    setShowSubmitModal(false);

    try {
      await api.post(`/exams/${examId}/submit`, {
        answers: Object.entries(answers).map(([questionId, answer]) => ({ questionId, answer })),
        force
      });
      toast.success(t('examRoom.toasts.submitSuccess'));
      disconnectSocket();
      navigate('/student/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || t('examRoom.toasts.submitFailed'));
      autoSubmitRef.current = false;
      setSubmitting(false);
    }
  };

  const handleTimeUp = () => {
    toast.error(t('examRoom.toasts.timeUp'));
    handleSubmit(true);
  };

  const getQuestionStatus = (q, idx) => {
    const qId = q._id || q.id;
    const isFlagged = flagged.has(qId);
    const isAnswered = answers[qId] !== undefined && answers[qId] !== '' &&
      !(Array.isArray(answers[qId]) && answers[qId].length === 0);
    const isCurrent = idx === currentIdx;

    if (isCurrent) return 'current';
    if (isFlagged) return 'flagged';
    if (isAnswered) return 'answered';
    return 'unanswered';
  };

  const answeredCount = questions.filter(q => {
    const qId = q._id || q.id;
    const ans = answers[qId];
    return ans !== undefined && ans !== '' && !(Array.isArray(ans) && ans.length === 0);
  }).length;

  // Waiting room — setup done but exam hasn't started yet
  if (waitingToStart) {
    const mm = String(Math.floor(secsUntilStart / 60)).padStart(2, '0');
    const ss = String(secsUntilStart % 60).padStart(2, '0');
    return (
      <div className="fixed inset-0 bg-slate-950 flex flex-col items-center justify-center px-4">
        {/* Top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-themePrimary" />

        <div className="w-full max-w-lg text-center space-y-8">
          {/* Exam name */}
          <div>
            <p className="text-themePrimary text-xs font-semibold uppercase tracking-widest mb-2">{t('examRoom.waitingRoom.label')}</p>
            <h1 className="text-2xl font-bold text-white">{lobbyInfo?.name || t('examRoom.waitingRoom.defaultExamName')}</h1>
            <p className="text-slate-400 text-sm mt-1">
              <Trans
                i18nKey="examRoom.waitingRoom.scheduledToBegin"
                values={{
                  time: lobbyInfo?.startTime ? new Date(lobbyInfo.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—',
                }}
              >
                Scheduled to begin at <span className="text-white font-medium">{'{{time}}'}</span>
              </Trans>
            </p>
          </div>

          {/* Countdown */}
          <div className="bg-slate-900 border border-slate-700 rounded-2xl px-8 py-6 inline-block mx-auto">
            <p className="text-slate-400 text-xs uppercase tracking-widest mb-3">{t('examRoom.waitingRoom.startsIn')}</p>
            <div className="text-6xl font-bold text-white tabular-nums font-mono tracking-tight">
              {mm}:{ss}
            </div>
            <p className="text-slate-500 text-xs mt-3">{t('examRoom.waitingRoom.autoLoadNote')}</p>
          </div>

          {/* Status checks */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-left space-y-2.5 max-w-xs mx-auto">
            <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1">{t('examRoom.waitingRoom.checklistTitle')}</p>
            {[
              { label: t('examRoom.waitingRoom.checklistIdentityPhoto'), ok: true },
              { label: t('examRoom.waitingRoom.checklistCameraReady'), ok: true },
              { label: t('examRoom.waitingRoom.checklistInstructionsReviewed'), ok: true },
              { label: t('examRoom.waitingRoom.checklistWaitingForExam'), ok: false, pending: true },
            ].map(({ label, ok, pending }) => (
              <div key={label} className="flex items-center gap-2.5 text-sm">
                {pending ? (
                  <Loader2 className="w-4 h-4 text-amber-400 animate-spin flex-shrink-0" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                )}
                <span className={pending ? 'text-amber-300' : 'text-slate-300'}>{label}</span>
              </div>
            ))}
          </div>

          <p className="text-slate-600 text-xs">
            {t('examRoom.waitingRoom.doNotClose')}
          </p>
        </div>

        {/* Keep webcam stream alive off-screen */}
        <div style={{ position: 'fixed', top: -9999, left: -9999, width: 1, height: 1, overflow: 'hidden', opacity: 0 }}>
          <video ref={webcamVideoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%' }} />
        </div>
      </div>
    );
  }

  if (!streamSetupComplete) {
    // Unified full-screen setup page — covers both permission + preview stages
    const cameraReady = setupStage === 'preview';

    return (
      <div className="fixed inset-0 bg-slate-950 flex flex-col overflow-hidden">
        {/* Top bar */}
        <div className="flex-shrink-0 bg-themePrimary px-6 py-3 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="text-white font-bold text-sm">{t('examRoom.setup.headerTitle')}</span>
              <span className="text-white/70 text-xs ml-3">
                {cameraReady ? (initialWebcamSnap ? t('examRoom.setup.step3') : t('examRoom.setup.step2')) : t('examRoom.setup.step1')}
              </span>
            </div>
          </div>
          {/* Step indicators */}
          <div className="flex items-center gap-2">
            {[1, 2, 3].map(s => {
              const active = s === 1 ? !cameraReady : s === 2 ? cameraReady && !initialWebcamSnap : !!initialWebcamSnap;
              const done = s === 1 ? cameraReady : s === 2 ? !!initialWebcamSnap : false;
              return (
                <div key={s} className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold border-2 transition-all ${done ? 'bg-green-500 border-green-400 text-white' : active ? 'bg-white border-white text-themePrimary' : 'bg-transparent border-white/30 text-white/40'}`}>
                  {done ? <CheckCircle2 className="w-4 h-4" /> : s}
                </div>
              );
            })}
          </div>
        </div>

        {/* Main content: left = camera, right = instructions */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden">

          {/* LEFT — Camera Panel */}
          <div className="w-full md:w-[42%] h-[40vh] md:h-auto flex-shrink-0 bg-slate-900 flex flex-col border-b md:border-b-0 md:border-r border-slate-800">
            {/* Camera viewport */}
            <div className="flex-1 relative overflow-hidden">
              {cameraReady ? (
                <>
                  {/* Live feed or captured photo */}
                  {initialWebcamSnap ? (
                    <img
                      src={initialWebcamSnap}
                      alt="Captured"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <video
                      ref={webcamVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ transform: 'scaleX(-1)' }}
                    />
                  )}
                  {/* Overlay frame guides */}
                  {!initialWebcamSnap && (
                    <div className="absolute inset-0 pointer-events-none">
                      {/* Face oval guide */}
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[55%] w-32 h-40 border-2 border-dashed border-white/40 rounded-full" />
                      <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                        <span className="bg-black/60 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm">
                          {t('examRoom.setup.positionFace')}
                        </span>
                      </div>
                    </div>
                  )}
                  {/* Captured badge */}
                  {initialWebcamSnap && (
                    <div className="absolute top-4 left-0 right-0 flex justify-center pointer-events-none">
                      <span className="bg-green-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        {t('examRoom.setup.photoCaptured')}
                      </span>
                    </div>
                  )}
                  {/* LIVE badge */}
                  {!initialWebcamSnap && (
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-red-600 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow">
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                      {t('examRoom.setup.live')}
                    </div>
                  )}
                </>
              ) : (
                /* No camera yet — dark placeholder */
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-slate-800 border-2 border-slate-700 flex items-center justify-center">
                    <Video className="w-10 h-10 text-slate-600" />
                  </div>
                  <div className="text-center px-6">
                    <p className="text-slate-400 text-sm font-medium">{t('examRoom.setup.cameraNotEnabled')}</p>
                    <p className="text-slate-600 text-xs mt-1">{t('examRoom.setup.cameraNotEnabledDesc')}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Camera action bar at bottom of left panel */}
            <div className="flex-shrink-0 bg-slate-900/90 backdrop-blur border-t border-slate-800 p-4 space-y-3">
              {!cameraReady ? (
                /* Step 1 action: grant camera */
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="agreeTerms"
                      checked={termsAgreed}
                      onChange={(e) => setTermsAgreed(e.target.checked)}
                      className="w-4 h-4 flex-shrink-0"
                      style={{ accentColor: 'var(--theme-primary, #4f46e5)' }}
                    />
                    <label htmlFor="agreeTerms" className="text-xs text-slate-400 cursor-pointer leading-snug">
                      <Trans i18nKey="examRoom.setup.agreeTerms">
                        I have read all instructions. I am using a <span className="text-white font-medium">regular browser window</span> (not incognito).
                      </Trans>
                    </label>
                  </div>
                  <Button
                    variant="primary"
                    fullWidth
                    icon={Video}
                    loading={requestingStreams}
                    disabled={!termsAgreed}
                    onClick={requestStreams}
                  >
                    {requestingStreams ? t('examRoom.setup.enablingCamera') : t('examRoom.setup.enableCamera')}
                  </Button>
                </div>
              ) : !initialWebcamSnap ? (
                /* Step 2 action: capture */
                <div className="space-y-2">
                  <p className="text-xs text-slate-400 text-center">{t('examRoom.setup.ensureLighting')}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        webcamStreamRef.current?.getTracks().forEach(t => t.stop());
                        webcamStreamRef.current = null;
                        setSetupStage('permissions');
                        setTermsAgreed(false);
                      }}
                      className="flex-shrink-0 flex items-center gap-1 border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 py-2.5 px-3 rounded-xl text-sm font-medium transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" /> {t('common.back')}
                    </button>
                    <Button variant="primary" className="flex-1" icon={Camera} loading={capturingPhoto} onClick={captureInitialSnapshots}>
                      {capturingPhoto ? t('examRoom.setup.capturing') : t('examRoom.setup.takePhoto')}
                    </Button>
                  </div>
                </div>
              ) : (
                /* Step 3 action: start exam */
                <div className="space-y-2">
                  <p className="text-xs text-green-400 text-center font-medium">{t('examRoom.setup.identityCaptured')}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setInitialWebcamSnap(null);
                        initialSnapsRef.current = { webcam: null };
                      }}
                      className="flex-shrink-0 flex items-center gap-1.5 border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 py-2.5 px-3 rounded-xl text-sm font-medium transition-colors"
                    >
                      <RotateCcw className="w-4 h-4" /> {t('examRoom.setup.retake')}
                    </button>
                    <Button variant="primary" className="flex-1 !bg-green-600 hover:!bg-green-500" icon={CheckCircle2} onClick={confirmAndStartExam}>
                      {lobbyInfo?.startTime && new Date(lobbyInfo.startTime) > new Date()
                        ? t('examRoom.setup.enterWaitingRoom')
                        : t('examRoom.setup.startExam')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Instructions Panel */}
          <div className="flex-1 overflow-y-auto bg-slate-50">
            <div className="p-6 space-y-5 max-w-2xl">

              {/* Page title */}
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {lobbyInfo?.name ? lobbyInfo.name : t('examRoom.setup.instructionsTitle')}
                </h2>
                {lobbyInfo?.startTime && (
                  <div className="mt-1 inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium px-2.5 py-1 rounded-full">
                    <Clock className="w-3.5 h-3.5" />
                    {t('examRoom.setup.startsAt', {
                      time: new Date(lobbyInfo.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                      duration: lobbyInfo.duration,
                    })}
                  </div>
                )}
                <p className="text-sm text-slate-500 mt-2">{t('examRoom.setup.readRulesNote')}</p>
              </div>

              {/* Critical: No Incognito */}
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <ShieldOff className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-red-700">{t('examRoom.setup.noIncognitoTitle')}</p>
                  <p className="text-xs text-red-600 mt-1 leading-relaxed">
                    <Trans i18nKey="examRoom.setup.noIncognitoDesc">
                      Incognito mode blocks camera access, real-time proctoring connections, and session storage — the exam will not function correctly. Close this tab and reopen in a <strong>regular browser window</strong>.
                    </Trans>
                  </p>
                </div>
              </div>

              {/* Environment */}
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center text-blue-600">
                    <Lightbulb className="w-3.5 h-3.5" />
                  </span>
                  {t('examRoom.setup.envRequirementsTitle')}
                </h3>
                <div className="space-y-2">
                  {[
                    { icon: Lightbulb, text: t('examRoom.setup.envLighting') },
                    { icon: Armchair, text: t('examRoom.setup.envBackground') },
                    { icon: VolumeX, text: t('examRoom.setup.envAlone') },
                    { icon: Smartphone, text: t('examRoom.setup.envNoPhones') },
                    { icon: BookX, text: t('examRoom.setup.envNoNotes') },
                    { icon: Eye, text: t('examRoom.setup.envFaceVisible') },
                  ].map(({ icon: ItemIcon, text }) => (
                    <div key={text} className="flex items-start gap-3 text-sm text-slate-700">
                      <ItemIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400" />
                      <span>{text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Browser & Technical */}
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <span className="w-6 h-6 bg-purple-100 rounded-md flex items-center justify-center text-purple-600">
                    <Lock className="w-3.5 h-3.5" />
                  </span>
                  {t('examRoom.setup.browserRulesTitle')}
                </h3>
                <div className="space-y-2">
                  {[
                    { icon: Globe, text: t('examRoom.setup.ruleBrowserType') },
                    { icon: Camera, text: t('examRoom.setup.ruleCameraMic') },
                    { icon: Lock, text: t('examRoom.setup.ruleFullscreen') },
                    { icon: ShieldOff, text: t('examRoom.setup.ruleNoTabSwitch') },
                    { icon: Keyboard, text: t('examRoom.setup.ruleDevTools') },
                    { icon: MousePointerClick, text: t('examRoom.setup.ruleNoCopyPaste') },
                  ].map(({ icon: ItemIcon, text }) => (
                    <div key={text} className="flex items-start gap-3 text-sm text-slate-700">
                      <ItemIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400" />
                      <span>{text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Violations */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <h3 className="text-sm font-bold text-amber-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> {t('examRoom.setup.violationsMonitoringTitle')}
                </h3>
                <ul className="space-y-1.5 text-xs text-amber-700">
                  <li className="flex items-start gap-2"><span className="flex-shrink-0 mt-0.5">•</span>{t('examRoom.setup.violationTabSwitch')}</li>
                  <li className="flex items-start gap-2"><span className="flex-shrink-0 mt-0.5">•</span>{t('examRoom.setup.violationRealtimeAlerts')}</li>
                  <li className="flex items-start gap-2"><span className="flex-shrink-0 mt-0.5">•</span>{t('examRoom.setup.violationAutoSubmit')}</li>
                  <li className="flex items-start gap-2"><span className="flex-shrink-0 mt-0.5">•</span>{t('examRoom.setup.violationWebcamSnapshots')}</li>
                </ul>
              </div>

              {/* Camera setup step */}
              <div className="bg-themePrimary/5 border border-themePrimary/20 rounded-xl p-4">
                <h3 className="text-sm font-bold text-themePrimary mb-2 flex items-center gap-2">
                  <Camera className="w-4 h-4" /> {t('examRoom.setup.cameraSetupTitle')}
                </h3>
                <ol className="space-y-1.5 text-xs text-themePrimary/90 list-none">
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 font-bold">1.</span>
                    <Trans i18nKey="examRoom.setup.cameraSetupStep1">
                      Check the box below the camera and click <strong>"Enable Camera & Microphone"</strong>
                    </Trans>
                  </li>
                  <li className="flex items-start gap-2"><span className="flex-shrink-0 font-bold">2.</span>{t('examRoom.setup.cameraSetupStep2')}</li>
                  <li className="flex items-start gap-2"><span className="flex-shrink-0 font-bold">3.</span>{t('examRoom.setup.cameraSetupStep3')}</li>
                  <li className="flex items-start gap-2">
                    <span className="flex-shrink-0 font-bold">4.</span>
                    <Trans i18nKey="examRoom.setup.cameraSetupStep4">
                      Click <strong>"Take Photo"</strong> for identity verification, then <strong>"Start Exam"</strong>
                    </Trans>
                  </li>
                </ol>
              </div>

            </div>
          </div>

        </div>

        {/* Hidden video ref to keep stream alive after snapshot is captured */}
        {initialWebcamSnap && (
          <div style={{ position: 'fixed', top: -9999, left: -9999, width: 1, height: 1, overflow: 'hidden', opacity: 0 }}>
            <video ref={webcamVideoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%' }} />
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <>
        <div className="min-h-screen bg-slate-900 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-white">
            <Spinner size="lg" className="text-themePrimary" />
            <p className="text-lg font-medium">{t('examRoom.loading')}</p>
          </div>
        </div>
        <div style={{ position: 'fixed', top: -9999, left: -9999, width: 640, height: 480, overflow: 'hidden', opacity: 0.01 }}>
          <video ref={webcamVideoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%' }} />
        </div>
      </>
    );
  }

  if (examLocked) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">{t('examRoom.examLocked.title')}</h2>
          <p className="text-slate-500 text-sm">
            {t('examRoom.examLocked.description')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">

      {/* Violation Warning Overlay */}
      {currentViolation && (
        <ViolationWarning
          violation={currentViolation}
          violationCount={violationCount}
          onDismiss={() => {
            setCurrentViolation(null);
            requestFullscreen();
          }}
        />
      )}

      {/* Submit Confirmation Modal */}
      <Modal
        open={showSubmitModal}
        onClose={() => { if (!submitting) setShowSubmitModal(false); }}
        size="sm"
        footer={
          <>
            <Button variant="outline" fullWidth onClick={() => setShowSubmitModal(false)}>
              {t('examRoom.submitModal.continueExam')}
            </Button>
            <Button variant="danger" fullWidth loading={submitting} onClick={() => handleSubmit(false)}>
              {submitting ? t('examRoom.submitModal.submitting') : t('examRoom.submitModal.submitNow')}
            </Button>
          </>
        }
      >
        <div className="text-center">
          <div className="w-14 h-14 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <AlertTriangle className="w-7 h-7 text-yellow-600" />
          </div>
          <h3 className="text-lg font-bold text-slate-800">{t('examRoom.submitModal.title')}</h3>
          <p className="text-slate-500 text-sm mt-2">
            {t('examRoom.submitModal.answeredSummary', { answered: answeredCount, total: questions.length })}
            {questions.length - answeredCount > 0 && (
              <span className="text-orange-600 font-medium"> {t('examRoom.submitModal.unanswered', { count: questions.length - answeredCount })}</span>
            )}
          </p>
          <p className="text-slate-400 text-xs mt-1">{t('examRoom.submitModal.cannotUndo')}</p>
        </div>
      </Modal>

      {/* Top Bar */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between flex-shrink-0 relative z-40">
        <div className="flex items-center gap-2 md:gap-4 overflow-hidden">
          <button
            onClick={() => setShowNavDrawer(true)}
            className="md:hidden p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="truncate">
            <h1 className="text-sm md:text-base font-bold text-slate-900 leading-tight truncate">{exam?.name}</h1>
            <p className="text-[10px] md:text-xs text-slate-500 truncate">{user?.name}</p>
          </div>
          {violationCount > 0 && (
            <div className="hidden sm:flex items-center gap-1 bg-red-50 border border-red-200 text-red-600 text-[10px] md:text-xs font-medium px-2 md:px-2.5 py-1 rounded-full whitespace-nowrap">
              <AlertTriangle className="w-3 md:w-3.5 h-3 md:h-3.5" />
              {isMobile ? `${violationCount} !` : t('examRoom.header.violation', { count: violationCount })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          {timeLeft > 0 && (
            <div className={`flex items-center gap-1.5 md:gap-2 px-2.5 md:px-4 py-1.5 md:py-2 rounded-lg font-mono text-base md:text-lg font-bold border-2 ${timeLeft <= 60
              ? 'bg-red-100 border-red-500 text-red-700 animate-pulse'
              : timeLeft <= 300
                ? 'bg-yellow-100 border-yellow-500 text-yellow-700'
                : 'bg-themePrimary/5 border-themePrimary/30 text-themePrimary'
              }`}>
              <Clock className="w-4 md:w-5 h-4 md:h-5 hidden xs:block" />
              <span className="tabular-nums">{formatTime(timeLeft)}</span>
            </div>
          )}
          <button
            onClick={() => { setShowMessages(v => !v); setUnreadCount(0); }}
            className="relative hidden sm:flex items-center gap-1.5 bg-themePrimary/10 hover:bg-themePrimary/20 border border-themePrimary/20 text-themePrimary text-sm font-medium px-3 py-2 rounded-lg transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            <span className="hidden md:inline">{t('examRoom.header.messages')}</span>
            {unreadCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>
          <Button variant="danger" size="sm" loading={submitting} onClick={() => setShowSubmitModal(true)}>
            {isMobile ? t('examRoom.header.submit') : t('examRoom.header.submitExam')}
          </Button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden relative">
        {/* Mobile Navigation Drawer Overlay */}
        {showNavDrawer && (
          <div
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 md:hidden"
            onClick={() => setShowNavDrawer(false)}
          />
        )}

        {/* Left Sidebar: Question Navigator */}
        <aside className={`
          fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200 flex flex-col z-50 transition-transform md:relative md:translate-x-0 md:w-56 md:z-10
          ${showNavDrawer ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <div className="p-4 border-b border-slate-100 flex items-center justify-between md:block">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider md:mb-2">{t('examRoom.nav.questions')}</p>
            <button
              onClick={() => setShowNavDrawer(false)}
              className="md:hidden p-1 text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 border-b border-slate-100 md:block hidden">
            <div className="flex items-center gap-2 text-xs flex-wrap">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-green-500" />
                <span className="text-slate-500">{t('examRoom.nav.legendAnswered')}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-yellow-400" />
                <span className="text-slate-500">{t('examRoom.nav.legendFlagged')}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-slate-200" />
                <span className="text-slate-500">{t('examRoom.nav.legendUnanswered')}</span>
              </div>
            </div>
          </div>
          <div className="p-3 flex-1 overflow-y-auto">
            <div className="grid grid-cols-4 md:grid-cols-4 gap-1.5">
              {questions.map((q, idx) => {
                const status = getQuestionStatus(q, idx);
                const bgMap = {
                  current: 'bg-themePrimary text-white ring-2 ring-themePrimary/30',
                  answered: 'bg-green-500 text-white',
                  flagged: 'bg-yellow-400 text-white',
                  unanswered: 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                };
                const statusLabelKey = {
                  current: 'legendCurrent',
                  answered: 'legendAnswered',
                  flagged: 'legendFlagged',
                  unanswered: 'legendUnanswered',
                }[status];
                return (
                  <button
                    key={q._id || q.id || idx}
                    onClick={() => { setCurrentIdx(idx); setShowNavDrawer(false); }}
                    className={`w-full aspect-square rounded-lg text-xs font-bold transition-all ${bgMap[status]}`}
                    title={t('examRoom.nav.questionTooltip', { num: idx + 1, status: t(`examRoom.nav.${statusLabelKey}`) })}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="p-4 border-t border-slate-100 bg-slate-50">
            <div className="text-xs text-slate-600 space-y-1">
              <div className="flex justify-between">
                <span>{t('examRoom.nav.answeredLabel')}</span>
                <span className="font-semibold text-green-600 hidden md:block">{answeredCount}</span>
                <span className="font-semibold text-green-600 md:hidden">{answeredCount}/{questions.length}</span>
              </div>
              <div className="justify-between hidden md:flex">
                <span>{t('examRoom.nav.remainingLabel')}</span>
                <span className="font-semibold text-orange-500">{questions.length - answeredCount}</span>
              </div>
            </div>
          </div>

          {/* Live Camera Preview */}
          <div className="flex-shrink-0 border-t border-slate-200 bg-slate-900">
            <div className="flex items-center justify-between px-3 py-1.5">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                <span className="text-xs font-semibold text-slate-400 tracking-wide">{t('examRoom.setup.live')}</span>
              </div>
              <span className="text-xs text-slate-500">{t('examRoom.nav.liveProctored')}</span>
            </div>
            <div className="relative overflow-hidden bg-black" style={{ aspectRatio: '4/3' }}>
              <video
                ref={webcamVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ transform: 'scaleX(-1)' }}
              />
              <div className="absolute bottom-1.5 right-1.5">
                <ShieldCheck className="w-4 h-4 text-white/30" />
              </div>
            </div>
          </div>
        </aside>

        {/* Main Question Area */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {currentQuestion ? (
            <div className="max-w-3xl mx-auto">
              {/* Question Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 md:gap-3">
                  <span className="text-xs md:text-sm font-bold text-slate-500">
                    {t('examRoom.question.questionOf', { current: currentIdx + 1, total: questions.length })}
                  </span>
                  <span className="hidden xs:inline-block text-[10px] md:text-xs bg-themePrimary/10 text-themePrimary px-2.5 py-0.5 rounded-full font-medium">
                    {currentQuestion.type}
                  </span>
                </div>
                <button
                  onClick={toggleFlag}
                  className={`flex items-center gap-1.5 text-xs font-medium px-2.5 md:px-3 py-1.5 rounded-lg border transition-colors ${flagged.has(currentQuestion._id || currentQuestion.id)
                    ? 'bg-yellow-50 border-yellow-300 text-yellow-700'
                    : 'bg-white border-slate-300 text-slate-600 hover:border-yellow-300 hover:text-yellow-600'
                    }`}
                >
                  <Flag className="w-4 h-4" fill={flagged.has(currentQuestion._id || currentQuestion.id) ? 'currentColor' : 'none'} />
                  <span className="hidden xs:inline">{flagged.has(currentQuestion._id || currentQuestion.id) ? t('examRoom.question.flagged') : t('examRoom.question.flag')}</span>
                </button>
              </div>

              {/* Question Card */}
              <div className="bg-white rounded-xl md:rounded-2xl shadow-sm border border-slate-200 p-4 md:p-6 mb-4">
                <p className="text-slate-900 text-sm md:text-base leading-relaxed font-medium mb-6">
                  {currentQuestion.text}
                </p>

                {/* MCQ / SINGLE_CHOICE */}
                {(currentQuestion.type === 'MCQ' || currentQuestion.type === 'SINGLE_CHOICE') && (
                  <div className="space-y-2 md:space-y-3">
                    {currentQuestion.options?.map((opt, oi) => {
                      const qId = currentQuestion._id || currentQuestion.id;
                      // Compare against _id (stable identity, safe under option
                      // shuffling) but fall back to index/text so an attempt
                      // already in progress from before this change still
                      // renders its saved selection correctly.
                      const isSelected = answers[qId] === opt._id || answers[qId] === oi || answers[qId] === opt.text;
                      return (
                        <label
                          key={opt._id || oi}
                          className={`flex items-center gap-3 p-3 md:p-4 rounded-xl border-2 cursor-pointer transition-all ${isSelected
                            ? 'border-themePrimary bg-themePrimary/5'
                            : 'border-slate-200 hover:border-themePrimary/40 hover:bg-slate-50'
                            }`}
                        >
                          <div className={`w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-themePrimary' : 'border-slate-300'
                            }`}>
                            {isSelected && <div className="w-2 md:w-2.5 h-2 md:h-2.5 bg-themePrimary rounded-full" />}
                          </div>
                          <input
                            type="radio"
                            className="hidden"
                            checked={isSelected}
                            onChange={() => handleAnswerChange(opt._id)}
                          />
                          <span className="text-sm text-slate-700">{opt.text || opt}</span>
                        </label>
                      );
                    })}
                  </div>
                )}

                {/* MULTIPLE_SELECT */}
                {currentQuestion.type === 'MULTIPLE_SELECT' && (
                  <div className="space-y-2 md:space-y-3">
                    <p className="text-[10px] md:text-xs text-slate-500 mb-2 md:mb-3">{t('examRoom.question.selectAllThatApply')}</p>
                    {currentQuestion.options?.map((opt, oi) => {
                      const qId = currentQuestion._id || currentQuestion.id;
                      const selected = answers[qId] || [];
                      // _id is the canonical identity (safe under option
                      // shuffling); also match plain index for an attempt
                      // already in progress from before this change.
                      const isSelected = selected.some(i => String(i) === String(opt._id) || String(i) === String(oi));
                      return (
                        <label
                          key={opt._id || oi}
                          className={`flex items-center gap-3 p-3 md:p-4 rounded-xl border-2 cursor-pointer transition-all ${isSelected
                            ? 'border-themePrimary bg-themePrimary/5'
                            : 'border-slate-200 hover:border-themePrimary/40 hover:bg-slate-50'
                            }`}
                        >
                          <div className={`w-4 h-4 md:w-5 md:h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-themePrimary bg-themePrimary' : 'border-slate-300'
                            }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                          </div>
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={isSelected}
                            onChange={() => handleMultiSelectChange(opt._id, oi)}
                          />
                          <span className="text-sm text-slate-700">{opt.text || opt}</span>
                        </label>
                      );
                    })}
                  </div>
                )}

                {/* TRUE_FALSE */}
                {currentQuestion.type === 'TRUE_FALSE' && (
                  <div className="flex flex-col sm:flex-row gap-3">
                    {['True', 'False'].map((val) => {
                      const qId = currentQuestion._id || currentQuestion.id;
                      const isSelected = answers[qId] === val || answers[qId] === val.toLowerCase();
                      const ValIcon = val === 'True' ? CheckCircle2 : X;
                      return (
                        <button
                          key={val}
                          onClick={() => handleAnswerChange(val)}
                          className={`flex-1 py-3 md:py-4 rounded-xl border-2 text-sm font-semibold transition-all flex items-center justify-center gap-2 ${isSelected
                            ? val === 'True'
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : 'border-red-400 bg-red-50 text-red-700'
                            : 'border-slate-200 text-slate-600 hover:border-themePrimary/40'
                            }`}
                        >
                          <ValIcon className="w-4 h-4" /> {val === 'True' ? t('mockTest.exam.true') : t('mockTest.exam.false')}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* DESCRIPTIVE */}
                {currentQuestion.type === 'DESCRIPTIVE' && (
                  <div>
                    <textarea
                      value={answers[currentQuestion._id || currentQuestion.id] || ''}
                      onChange={(e) => handleAnswerChange(e.target.value)}
                      placeholder={t('examRoom.question.descriptivePlaceholder')}
                      rows={6}
                      className="w-full border-2 border-slate-200 rounded-xl p-3 md:p-4 text-sm text-slate-800 focus:outline-none focus:border-themePrimary resize-none transition-colors"
                    />
                    <p className="text-[10px] md:text-xs text-slate-400 mt-1 text-right">
                      {t('examRoom.question.charactersCount', { count: (answers[currentQuestion._id || currentQuestion.id] || '').length })}
                    </p>
                  </div>
                )}
              </div>

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
                  disabled={currentIdx === 0}
                  className="flex items-center justify-center gap-1.5 px-3 md:px-4 py-2.5 border border-slate-300 rounded-lg text-xs md:text-sm font-medium text-slate-700 disabled:opacity-40 hover:bg-slate-50 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden xs:inline">{t('examRoom.question.previous')}</span>
                </button>

                <div className="flex-1 md:flex-none">
                  {currentIdx < questions.length - 1 ? (
                    <Button
                      variant="primary"
                      fullWidth
                      icon={ChevronRight}
                      iconPosition="right"
                      onClick={() => setCurrentIdx(i => Math.min(questions.length - 1, i + 1))}
                    >
                      {t('examRoom.question.saveAndNext')}
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      icon={CheckCircle2}
                      className="!bg-green-600 hover:!bg-green-700"
                      onClick={() => setShowSubmitModal(true)}
                    >
                      {t('examRoom.question.reviewAndSubmit')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto text-center py-16">
              <p className="text-slate-500">{t('examRoom.question.noQuestions')}</p>
            </div>
          )}
        </main>

        {/* Right Sidebar: Proctor Messages */}
        {showMessages && (
          <aside className={`
            fixed inset-y-0 right-0 w-72 bg-white border-l border-slate-200 flex flex-col z-50 transition-transform md:relative md:translate-x-0 md:z-10 shadow-2xl md:shadow-none
            ${showMessages ? 'translate-x-0' : 'translate-x-full'}
          `}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-themePrimary/5">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-themePrimary" />
                <span className="text-sm font-semibold text-themePrimary">{t('examRoom.messagesPanel.title')}</span>
              </div>
              <button
                onClick={() => setShowMessages(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {proctorMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                  <MessageSquare className="w-10 h-10 text-slate-200 mb-3" />
                  <p className="text-xs text-slate-400">{t('examRoom.messagesPanel.noMessages')}</p>
                </div>
              ) : (
                proctorMessages.map((msg) => {
                  const isWarning = msg.type === 'WARNING' || msg.content?.toLowerCase().startsWith('warning');
                  return (
                    <div
                      key={msg.id}
                      className={`rounded-xl p-3 border ${isWarning
                        ? 'bg-yellow-50 border-yellow-200'
                        : 'bg-themePrimary/5 border-themePrimary/20'
                        }`}
                    >
                      <div className="flex items-center gap-1.5 mb-1.5">
                        {isWarning ? (
                          <AlertTriangle className="w-3.5 h-3.5 text-yellow-600 flex-shrink-0" />
                        ) : (
                          <MessageSquare className="w-3.5 h-3.5 text-themePrimary flex-shrink-0" />
                        )}
                        <span className={`text-xs font-semibold ${isWarning ? 'text-yellow-700' : 'text-themePrimary'}`}>
                          {isWarning ? t('examRoom.messagesPanel.warning') : t('examRoom.messagesPanel.proctor')}
                        </span>
                        <span className="text-xs text-slate-400 ml-auto">
                          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className={`text-xs leading-relaxed ${isWarning ? 'text-yellow-800' : 'text-slate-700'}`}>
                        {msg.content}
                      </p>
                    </div>
                  );
                })
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default ExamRoom;
